import { useCallback, useMemo } from 'react';

import { type Asset } from '@/services/db/asset/entity';

import { TOP_LEVEL_FOLDER_PATH } from '../store';
import { type FolderPath } from '../types';

export function useAssetNavigation(parents: Asset[] = [], onSetParents?: (parents: Asset[]) => void) {
  const parentFolderId = useMemo(() => {
    return parents.length > 0 ? parents.at(-1)?.id ?? null : null;
  }, [parents]);

  const folderPaths = useMemo(() => {
    const paths: FolderPath[] = [];
    const visited = new Set<string>();

    let folderId: string | null = parentFolderId;
    while (folderId !== null) {
      if (visited.has(folderId)) break;
      visited.add(folderId);

      const parentFolder = parents.find((parent) => parent.id === folderId);
      const nextParentFolderId = parentFolder?.parent_id ?? null;

      if (parentFolder != null) {
        paths.unshift({ name: parentFolder.name, id: folderId });
      }

      folderId = nextParentFolderId;
    }

    paths.unshift(TOP_LEVEL_FOLDER_PATH);
    return paths;
  }, [parents, parentFolderId]);

  const currentFolderName = useMemo(() => {
    return folderPaths.at(-1)?.name ?? 'All files';
  }, [folderPaths]);

  const navigateToFolder = useCallback(
    (folderId: string | null, folderAsset?: Asset) => {
      if (folderId === null) {
        onSetParents?.([]);
        return;
      }

      const folderIndex = parents.findIndex((p) => p.id === folderId);
      if (folderIndex !== -1) {
        onSetParents?.(parents.slice(0, folderIndex + 1));
        return;
      }

      if (folderAsset != null) {
        onSetParents?.([...parents, folderAsset]);
      }
    },
    [parents, onSetParents],
  );

  return { parentFolderId, currentFolderName, folderPaths, navigateToFolder };
}
